import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { FileItem, FileUploader } from 'ng2-file-upload';
import { ToastrService } from 'ngx-toastr';
import { Subscription, of } from 'rxjs';
import { catchError, debounceTime, filter, switchMap, tap } from 'rxjs/operators';
import { AdminChurchService } from 'src/app/_core/api/admin-church.service';
import { AdminPartnerOrganizationService } from 'src/app/_core/api/admin-partner-organization.service';
import { UploaderApiService } from 'src/app/_core/api/uploader-api.service';
import { FileType } from 'src/app/_core/constants/FileType';
import { ModalActions, ModalEmitActions, Modals } from 'src/app/_core/constants/Modals';
import { ToastrMessages } from 'src/app/_core/constants/ToastrMessages';
import ComputePayload from 'src/app/_core/helpers/ComputePayload';
import { CustomValidators } from 'src/app/_core/helpers/CustomValidators.helpers';
import FileUtils from 'src/app/_core/helpers/FileUtils';
import Utils from 'src/app/_core/helpers/Utils';
import { AdminPartnerOrganizationDetails } from 'src/app/_core/models/AdminChurches';
import { FileDetails } from 'src/app/_core/models/FileInfo';
import { ResponseObject } from 'src/app/_core/models/GenericObject';
import { Location } from 'src/app/_core/models/Location';
import { ModalFileResponse, ModalRequest, ModalResponse } from 'src/app/_core/models/ModalEvent';
import { Region, RegionDto } from 'src/app/_core/models/Region';
import { UploadPromiseResponse } from 'src/app/_core/models/Upload';
import { ModalsService } from 'src/app/_core/services/modals.service';
import { UploadService } from 'src/app/_core/services/upload.service';

@Component({
  selector: 'app-add-partner-organization',
  templateUrl: './add-partner-organization.component.html',
  styleUrls: ['./add-partner-organization.component.scss'],
})
export class AddPartnerOrganizationComponent implements OnInit, OnDestroy {
  subscription: Subscription = new Subscription();
  uploader: FileUploader;
  modalRequest: ModalRequest;
  modals = Modals;
  modalActions = ModalActions;
  modalEmitActions = ModalEmitActions;
  addPartnerOrganizationForm: UntypedFormGroup;
  partnerOrganizationDetails: AdminPartnerOrganizationDetails;
  partnerOrganizationPhoto: FileDetails = new FileDetails(null, null, null, null);
  liveSearchMainlandResults: Location[] = [];
  submitted: boolean;
  loading: boolean;
  selectedTabIndex: number = 0;

  // prettier-ignore
  constructor(
    private formBuilder: UntypedFormBuilder,
    private adminChurchService: AdminChurchService,
    private adminPartnerOrganizationService: AdminPartnerOrganizationService,
    private dialogRef: MatDialogRef<AddPartnerOrganizationComponent>,
    private uploaderApiService: UploaderApiService,
    private modalService: ModalsService,
    private toastr: ToastrService,
    private uploadService: UploadService,
    private breakpointObserver: BreakpointObserver,
  ) {
    this.subscription.add(this.modalService.modalResponse$.subscribe((response: ModalFileResponse) => {
      this.handleModalResponse(response);
    }));

    this.subscription.add(this.uploadService.uploaderObservable.subscribe(data =>
      data.progress ? this.setProgress(data.result) : this.setFileFields(data.result)
    ));
  }

  ngOnInit(): void {
    this.uploader = this.uploadService.uploaderInstance;
    this.modalRequest = this.modalService?.params;
    this.createForm();
    if ([ModalActions.EDIT_PARTNER_ORGANIZATION, ModalActions.EDIT_PARTNER_ORGANIZATION_REMOTELY].includes(this.modalRequest.scope)) {
      this.getPartnerOrganizationDetails();
    }
    if ([ModalActions.EDIT_PARTNER_ORGANIZATION, ModalActions.ADD_PARTNER_ORGANIZATION].includes(this.modalRequest.scope)) {
      this.subscription.add(
        this.breakpointObserver.observe('(max-width: 767px)').subscribe((result: BreakpointState) => {
          result.matches ? this.dialogRef.updateSize('100vw', '100vh') : this.dialogRef.updateSize('50vw', '100vh');
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  getPartnerOrganizationDetails(): void {
    this.loading = true;
    this.adminPartnerOrganizationService.getPartnerOrganization(this.modalRequest.uuid).subscribe({
      next: (res: ResponseObject<AdminPartnerOrganizationDetails>) => {
        this.partnerOrganizationDetails = res.response;
        this.prefillForm();
        this.setPartnerOrganizationImage();
        this.loading = false;
      },
      error: () => {
        this.toastr.error(...ToastrMessages.BASIC_ERROR);
        this.loading = false;
      },
    });
  }

  createForm(): void {
    this.addPartnerOrganizationForm = this.formBuilder.group({
      partnerOrganizationUuid: [null],
      partnerOrganizationName: [null, [Validators.required]],
      mainland: [null, [Validators.required, CustomValidators.mainland]],
      website: [null, [CustomValidators.website]],
      partnerOrganizationPictureUrl: [null],
      // visible: [true, [Validators.required]],
      description: [null, [Validators.maxLength(500)]],
      headline: [null, [Validators.maxLength(150)]],
    });

    this.mainlandLiveSearch();
  }

  prefillForm(): void {
    this.addPartnerOrganizationForm.patchValue({
      partnerOrganizationUuid: this.partnerOrganizationDetails.uuid,
      partnerOrganizationName: this.partnerOrganizationDetails.name,
      mainland: new Location(this.partnerOrganizationDetails.mainland),
      website: this.partnerOrganizationDetails.website,
      partnerOrganizationPictureUrl: this.partnerOrganizationDetails.imageUrl,
      // visible: this.partnerOrganizationDetails.visible,
      description: this.partnerOrganizationDetails.description,
      headline: this.partnerOrganizationDetails.headline,
    });
  }

  submit(): void {
    if (this.addPartnerOrganizationForm.pristine && this.addPartnerOrganizationForm.untouched) {
      Utils.clearErrorsOfFormGroup(this.addPartnerOrganizationForm);
      this.toastr.info(...ToastrMessages.NO_CHANGES);
      return;
    }

    this.addPartnerOrganizationForm.markAllAsTouched();
    this.submitted = true;
    Utils.checkValueAndValidityOfFormGroup(this.addPartnerOrganizationForm);

    if (this.addPartnerOrganizationForm.invalid) {
      this.toastr.error(...ToastrMessages.MISSING_REQUIRED_FIELDS);
      return;
    }

    this.loading = true;
    const payload = ComputePayload.createUpdatePartnerOrganization(this.addPartnerOrganizationForm.value, this.partnerOrganizationDetails?.verified);
    this.adminPartnerOrganizationService.createUpdatePartnerOrganization(payload).subscribe({
      next: (res) => {
        if ([ModalActions.EDIT_PARTNER_ORGANIZATION, ModalActions.EDIT_PARTNER_ORGANIZATION_REMOTELY].includes(this.modalRequest.scope)) {
          this.toastr.success(...ToastrMessages.PARTNER_ORGANIZATION_SUCCESSFULLY_EDITED);
        } else {
          this.toastr.success(...ToastrMessages.PARTNER_ORGANIZATION_SUCCESSFULLY_ADDED);
        }
        if (!this.modalRequest.uuid) this.modalRequest.uuid = res.response;
        this.modalRequest.payload = this.partnerOrganizationName.value;
        this.loading = false;
        this.emit(ModalEmitActions.PARTNER_ORGANIZATION_ADDED);
      },
      error: () => {
        this.toastr.error(...ToastrMessages.BASIC_ERROR);
        this.loading = false;
      },
    });
  }

  smallModalSubmit(): void {
    if (this.selectedTabIndex === 0) {
      if (this.addPartnerOrganizationForm.pristine && this.addPartnerOrganizationForm.untouched) {
        this.toastr.info(...ToastrMessages.NO_CHANGES);
        return;
      }
      this.addPartnerOrganizationForm.markAllAsTouched();
      this.submitted = true;
      if (this.addPartnerOrganizationForm.invalid) {
        this.toastr.error(...ToastrMessages.MISSING_REQUIRED_FIELDS);
        return;
      }
      this.selectedTabIndex++;
    } else {
      this.submit();
    }
  }

  mainlandLiveSearch(): void {
    this.mainland.valueChanges
      .pipe(
        filter((value) => typeof value === 'string' && value.trim().length > 0),
        debounceTime(500),
        tap(() => (this.liveSearchMainlandResults = [])),
        switchMap((value: string) => this.adminChurchService.searchMainland(value).pipe(catchError((err) => of(err))))
      )
      .subscribe((res: ResponseObject<RegionDto[]>) => {
        const regions = res.response.map((regionData) => new Region(regionData));
        regions.forEach((region) => {
          region.subRegions.forEach((subRegion) => (this.liveSearchMainlandResults = [...this.liveSearchMainlandResults, ...subRegion.locations]));
        });
      });
  }

  handleImageUpload(): void {
    if (this.partnerOrganizationPhoto?.path) {
      this.uploaderApiService
        .deleteImage({ fileType: FileType.PARTNER_ORGANIZATION_IMAGE, fileUuid: this.partnerOrganizationUuid.value })
        .subscribe(() => {
          this.partnerOrganizationPhoto = new FileDetails(null, null, null, null);
          this.partnerOrganizationPictureUrl.setValue(null);
          this.partnerOrganizationPictureUrl.markAsDirty();
        });
    } else {
      this.modalService.openModal(Modals.CROP_IMAGE, {
        aspectRatio: 1,
        fileType: FileType.PARTNER_ORGANIZATION_IMAGE,
        idx: null,
      });
    }
  }

  //Upload Image
  uploadImageAfterCrop(payload: string, fileType: FileType, target: string): void {
    const fileToUpload: FileItem = this.uploader.queue[this.uploader.queue.length - 1];
    fileToUpload.formData.fileType = fileType;
    fileToUpload.formData.entityUuid = this.modalRequest?.uuid;
    fileToUpload.formData.formControlName = target;
    fileToUpload._file = FileUtils.convertB64ToImage(payload, fileToUpload);
    this.partnerOrganizationPhoto.pendingUpload += 1;
    this.uploadService.uploadFile();
  }

  setProgress(response: UploadPromiseResponse): void {
    this.partnerOrganizationPhoto.progress = +response.response;
  }

  setFileFields(response: UploadPromiseResponse): void {
    const responseData = JSON.parse(response.response);
    const parsedData = responseData.response;
    const fileName = FileUtils.computeFileName(response.fileItem.file.name);

    if (responseData.hasOwnProperty('error')) {
      this.toastr.error(...ToastrMessages.BASIC_ERROR);
      this.partnerOrganizationPhoto.pendingUpload -= 1;
      return;
    }

    this.partnerOrganizationUuid.patchValue(parsedData.objectUuid);
    this.partnerOrganizationPictureUrl.patchValue(parsedData.url);
    this.partnerOrganizationPhoto.uuid = parsedData.objectUuid;
    this.partnerOrganizationPhoto.name = fileName;
    this.partnerOrganizationPhoto.path = parsedData.url;
    this.partnerOrganizationPhoto.fileType = FileType.PARTNER_ORGANIZATION_IMAGE;
    this.partnerOrganizationPhoto.isFormatValid = true;
    this.partnerOrganizationPhoto.pendingUpload -= 1;
  }

  handleModalResponse(response: ModalFileResponse): void {
    if (response?.confirmed) {
      switch (response.modalRequest.scope) {
        case ModalActions.CROP:
          this.uploadImageAfterCrop(response.payload, response.fileType, response.target);
          break;
        default:
          break;
      }
    }
  }

  setPartnerOrganizationImage(): void {
    if (this.partnerOrganizationDetails.imageUrl) {
      this.partnerOrganizationPhoto.path = this.partnerOrganizationDetails.imageUrl;
    }
  }

  // openCropModal(): void {
  //   this.modalService.openModal(Modals.CROP_IMAGE, { aspectRatio: 1, fileType: FileType.PARTNER_ORGANIZATION_IMAGE });
  // }

  //Hellper methods
  close(): void {
    this.dialogRef.close();
  }
  displayFn(item: Location): string | undefined {
    if (!item) return '';
    return `${item.subRegion}, ${item.name}`;
  }
  navigate(url: string): void {
    Utils.navigate(url);
  }
  emit(modalEmitAction: ModalEmitActions): void {
    const response = new ModalResponse(true, this.modalRequest, modalEmitAction);
    this.modalService.emitResponse(response);
    this.close();
  }
  smallModalBack(): void {
    if (this.selectedTabIndex === 1) this.selectedTabIndex--;
    else this.close();
  }

  //Getters
  get isSidePanel(): boolean {
    return [ModalActions.ADD_PARTNER_ORGANIZATION, ModalActions.EDIT_PARTNER_ORGANIZATION].includes(this.modalRequest.scope);
  }
  get partnerOrganizationUuid(): AbstractControl {
    return this.addPartnerOrganizationForm.get('partnerOrganizationUuid');
  }
  get partnerOrganizationName(): AbstractControl {
    return this.addPartnerOrganizationForm.get('partnerOrganizationName');
  }
  get mainland(): AbstractControl {
    return this.addPartnerOrganizationForm.get('mainland');
  }
  get website(): AbstractControl {
    return this.addPartnerOrganizationForm.get('website');
  }
  get partnerOrganizationPictureUrl(): AbstractControl {
    return this.addPartnerOrganizationForm.get('partnerOrganizationPictureUrl');
  }
  // get visible(): AbstractControl {
  //   return this.addPartnerOrganizationForm.get('visible');
  // }
  get description(): AbstractControl {
    return this.addPartnerOrganizationForm.get('description');
  }
  get headline(): AbstractControl {
    return this.addPartnerOrganizationForm.get('headline');
  }
}
